import { Typography } from '@euler/app/components/typography';
import { useAiChatAttached } from '@euler/app/flows/ai-suite/chat/AiChatActions';
import {
  ChatCard,
  ChatCardBadge,
} from '@euler/app/flows/ai-suite/components/ChatCard';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  BiChangeSetDetail,
  BiChangeSetOnlyOpKind,
} from '@euler/model/bi/change-set';
import { Undo2, WandSparkles } from 'lucide-react-native';
import { memo, useMemo } from 'react';
import { View } from 'react-native';
import { ChangeActions } from './ChangeActions';
import { ChangeLine } from './ChangeLine';
import { BiChangePhase, biChangePhase } from './functions/biChangePhase';
import { biChangeEntryText } from './functions/biChangeText';
import { useBiGhost } from './functions/useBiGhost';

const kBadges: Record<BiChangePhase, { label: string; color: string }> = {
  [BiChangePhase.Pending]: { label: '待确认', color: aiColors.primary },
  [BiChangePhase.Applied]: { label: '已应用', color: aiColors.green },
  [BiChangePhase.Discarded]: {
    label: '已放弃',
    color: aiColors.mutedForeground,
  },
  [BiChangePhase.Stale]: { label: '已失效', color: aiColors.amber },
  [BiChangePhase.Expired]: { label: '已过期', color: aiColors.mutedForeground },
};

/**
 * A proposed change to a dashboard: the summary, the diff line by line,
 * and its phase. While pending it is drawn as ghosts on the dashboard the
 * chat is open on.
 */
export const ChangesCard = memo(({ detail }: { detail: BiChangeSetDetail }) => {
  const { rpx } = useResponsiveContext();
  const attached = useAiChatAttached();
  const phase = biChangePhase(detail, Date.now());
  const onHost = useBiGhost(detail, phase === BiChangePhase.Pending);
  const { changeSet, dashboard, changes, preview } = detail;
  const undo = changeSet.ops[0]?.op === BiChangeSetOnlyOpKind.Undo;
  // What a placement is relative to: the preview names every card while
  // pending; once settled, the diff's own cards are what is left.
  const titles = useMemo(
    () =>
      new Map([
        ...changes.flatMap(entry =>
          entry.cardId ? [[entry.cardId, entry.title] as const] : [],
        ),
        ...(preview?.cards ?? []).map(card => [card.id, card.title] as const),
      ]),
    [changes, preview],
  );
  const badge = kBadges[phase];
  return (
    <ChatCard
      icon={undo ? Undo2 : WandSparkles}
      color={aiColors.amber}
      title={undo ? '撤销上一步修改' : '看板修改建议'}
      subtitle={`「${dashboard.name}」`}
      badge={<ChatCardBadge label={badge.label} color={badge.color} />}
    >
      {changeSet.summary ? (
        <Typography.Text
          size={rpx(13)}
          lineHeight={rpx(20)}
          color={aiColors.mutedForeground}
        >
          {changeSet.summary}
        </Typography.Text>
      ) : null}
      {changes.length ? (
        <View style={{ gap: rpx(6) }}>
          {changes.map((entry, index) => (
            <ChangeLine
              key={`${entry.kind}:${entry.cardId ?? entry.draftId}:${index}`}
              kind={entry.kind}
              text={biChangeEntryText(entry, titles)}
            />
          ))}
        </View>
      ) : null}
      <ChangeActions
        detail={detail}
        phase={phase}
        attached={attached}
        onHost={onHost}
      />
    </ChatCard>
  );
});
